import React, { ReactNode } from 'react'
import { Dropdown, QuestionVariant } from '../../model/general'
import {
	ClientQuestion,
	getImageDropdownKey,
	QuestionChoice,
} from '@mission.io/question-toolkit'
import { CloseIcon } from '../teacher-header/TeacherHeader'
import { TextImageField } from '../text-image-field/TextImageField'
import { FaTrash } from 'react-icons/fa'
import { DeleteChoice } from '../../stories/multiple-choice/Teacher'
import {
	AddButton,
	CheckboxButton,
	InputGroup,
	QuestionOption,
	QuestionOptionImage,
} from '../../assets/styles'
import { QuestionOptionIcon, getQuestionOptionIndex } from '../../utils'
import 'styled-components/macro'
import { styled } from 'styled-components'
import OptionalMarkdown from '../Markdown/OptionalMarkdown'
import classNames from 'classnames'

interface TabsProps {
	indexSelected: number
	handleChange: (index: number) => void
	handleClose?: (id: string) => void
	question?: ClientQuestion | null | undefined
}

//This component create a group of Tab for each Dropdown in a Teacher Question
export function DropdownTabs({
	question,
	indexSelected,
	handleChange,
	handleClose,
}: TabsProps): JSX.Element | null {
	if (!question) {
		return null
	}

	let dropdownIds: string[] = []
	if (question.type === QuestionVariant.CLOZE_DROPDOWN) {
		dropdownIds = question.dropdowns.map(
			({ templateResponseKey }) => templateResponseKey
		)
	} else if (question.type === QuestionVariant.IMAGE_LABELING) {
		dropdownIds = question.dropdowns.map(dropdown =>
			getImageDropdownKey(dropdown)
		)
	}

	return (
		<div role="tablist" className="my-2 flex max-w-full overflow-x-auto">
			{dropdownIds.map((dropdownId, index: number) => {
				const isSelected = indexSelected === index
				return (
					<button
						role="tab"
						className={classNames(
							`top-1 right-1 w-full p-2 rounded-t-lg`,
							isSelected &&
								'font-semibold border-b-2 border-b-brand-horizon-blue bg-brand-nebula text-brand-space-blue'
						)}
						key={`dropdown-${index}`}
						onClick={() => handleChange(index)}>
						<div css="position: relative; min-width: 3em;">
							<div role="tab">{index + 1}</div>
							{isSelected && handleClose && (
								<IconWrapper>
									<CloseIconTab
										role="icon"
										aria-label="CloseIcon"
										onClick={(event: any) => {
											event.stopPropagation()
											handleClose(dropdownId)
										}}
									/>
								</IconWrapper>
							)}
						</div>
					</button>
				)
			})}
		</div>
	)
}

interface TabPanelProps {
	readOnly?: boolean
	dropdowns: Dropdown[]
	indexSelected: number
	handleAddChoice?: (index: number) => void
	handleDeleteChoice?: (pos: number, index: number) => void
	handleAnswerChange?: (pos: number, index: number) => void
	handleChange?: (pos: number, value: string, index: number) => void
	autoFocus?: boolean
}

//This component create the body for the Tab selected. It have to use with DropdownTabs component.
export function TabPanel({
	readOnly = false,
	autoFocus = false,
	indexSelected,
	dropdowns,
	handleAddChoice,
	handleAnswerChange,
	handleDeleteChoice,
	handleChange,
}: TabPanelProps): ReactNode {
	const choicesHasText = Boolean(
		dropdowns[indexSelected]?.choices.find(
			(choice: QuestionChoice) => choice?.text !== ''
		)
	)

	if (readOnly)
		return (
			<div>
				{dropdowns[indexSelected]?.choices.map(
					(choice: QuestionChoice, idx: number) => {
						const image = choice.media?.[0]?.url

						return (
							<QuestionOption
								key={String(choice._id) || `${idx}-question-option`}
								$isReadOnly={true}
								$chosen={false}
								$correct={choice.answer}>
								<QuestionOptionIcon
									studentChoice={false}
									correctOption={choice.answer}
								/>
								{getQuestionOptionIndex(idx) + ':'}&nbsp;
								{choicesHasText ? (
									choice.text ? (
										<OptionalMarkdown>{choice.text}</OptionalMarkdown>
									) : (
										'(No text provided)'
									)
								) : null}
								{image && (
									<QuestionOptionImage
										alt={`Choice ${idx + 1} Image`}
										src={image}
									/>
								)}
							</QuestionOption>
						)
					}
				)}
			</div>
		)
	else
		return (
			indexSelected > -1 && (
				<div>
					{dropdowns[indexSelected]?.choices.map(
						(choice: QuestionChoice, i: number) => (
							<InputGroup key={`id-${i}`}>
								<CheckboxButton
									onClick={() =>
										handleAnswerChange && handleAnswerChange(i, indexSelected)
									}
									selected={choice.answer}
								/>
								<TextImageField
									type="text"
									placeholder="Type Answer..."
									value={choice.text}
									handleAddChoice={
										i + 1 === dropdowns[indexSelected]?.choices.length
											? () => handleAddChoice && handleAddChoice(indexSelected)
											: undefined
									}
									autoFocus={
										autoFocus &&
										i + 1 === dropdowns[indexSelected]?.choices.length
									}
									onChange={(e: any) =>
										handleChange &&
										handleChange(i, e.target.value, indexSelected)
									}
									media={choice.media}
								/>
								<div css="display: flex;">
									<DeleteChoice
										aria-label="DeleteChoiceIcon"
										onClick={() =>
											handleDeleteChoice && handleDeleteChoice(i, indexSelected)
										}>
										<FaTrash size={18} />
									</DeleteChoice>
								</div>
							</InputGroup>
						)
					)}
					{dropdowns[indexSelected] && (
						<AddButton
							onClick={() => handleAddChoice && handleAddChoice(indexSelected)}>
							Add Another Answer
						</AddButton>
					)}
				</div>
			)
		)
}

const IconWrapper = styled.div`
	border: none;
	z-index: 3;
	top: 0;
	background: transparent;
	position: absolute;
	padding: 0;
	border-radius: 50%;
	right: 0;
	height: fit-content;
	display: flex;
`

const CloseIconTab = styled(CloseIcon)`
	background: #b9bed6;
	color: ${({ theme }) => theme.primary};
	transform: scale(1);
	position: initial;
`
